import type { RefObject } from 'react';

import { useEffect, useRef } from 'react';

import { type BugReportForm, getDefaultFormValues } from './useBugReportForm';

type Params = {
  readonly derivedName: string | null | undefined;
  readonly form: BugReportForm;
  readonly isOpen: boolean;
  readonly setIsVideoLinkVisible: (visible: boolean) => void;
  readonly titleInputRef: RefObject<HTMLInputElement | null>;
};

/**
 * Side-effect-only hook that keeps the bug-report form in sync with the
 * dialog's open state and the lazily-loaded reporter name.
 *
 * - On open: reset to draft (or derived defaults) atomically, sync the
 *   video-link visibility flag, then focus the title input on the next frame.
 * - When user data arrives mid-dialog: backfill `reporterName` if the user
 *   hasn't already typed something there.
 *
 * Lives outside `useBugReport` so the orchestrator stays focused on glue
 * and so these effects can be reasoned about (and tested) on their own.
 */
export function useBugReportFormHydration({
  derivedName,
  form,
  isOpen,
  setIsVideoLinkVisible,
  titleInputRef,
}: Params): void {
  // Read inside the hydration effect via ref so the effect only re-runs when
  // the dialog opens — not when user data arrives mid-typing.
  const derivedNameRef = useRef(derivedName);
  derivedNameRef.current = derivedName;

  // Hydrate form from draft (or derived name) every time the dialog opens.
  // A single `reset(values)` is atomic: one store update, one validator run,
  // no observable half-hydrated state for subscribers (e.g. the draft saver).
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const defaults = getDefaultFormValues({
      derivedName: derivedNameRef.current,
    });

    form.reset(defaults);
    setIsVideoLinkVisible(Boolean(defaults.videoLink));

    const raf = requestAnimationFrame(() => {
      titleInputRef.current?.focus();
    });

    return () => cancelAnimationFrame(raf);
  }, [form, isOpen, setIsVideoLinkVisible, titleInputRef]);

  // Backfill reporterName when user data arrives after the dialog was opened.
  useEffect(() => {
    if (!isOpen || !derivedName) {
      return;
    }

    if (!form.store.state.values.reporterName) {
      form.setFieldValue('reporterName', derivedName);
    }
  }, [derivedName, form, isOpen]);
}
